import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { isRTL } from '../../../../utils/internationalization';

import css from './index.module.scss';

export const RIGHT_PANEL_WIDTH_VAR = '--right-panel-expanded-width';

export type ResizerProps = {
  elemRef: React.RefObject<HTMLDivElement> | React.MutableRefObject<HTMLDivElement | null>;
  locale?: string;
  minWidth: number;
  maxWidth: number;
  onResizeEnd?: (width: number) => void;
  onResizingChange?: (isResizing: boolean) => void;
};

export const Resizer: FunctionComponent<ResizerProps> = ({
  elemRef,
  locale,
  minWidth,
  maxWidth,
  onResizeEnd,
  onResizingChange,
}) => {
  const [isResizing, setIsResizing] = useState(false);

  const applyWidth = useCallback(
    (clientX: number) => {
      const node = elemRef.current;
      if (!node) {
        return;
      }
      const rect = node.getBoundingClientRect();
      const rtl = isRTL(locale);
      const raw = rtl ? clientX - rect.left : rect.right - clientX;
      const next = Math.min(Math.max(raw, minWidth), maxWidth);
      node.style.setProperty(RIGHT_PANEL_WIDTH_VAR, `${next}px`);
    },
    [elemRef, locale, minWidth, maxWidth],
  );

  useEffect(() => {
    onResizingChange?.(isResizing);
  }, [isResizing, onResizingChange]);

  useEffect(() => {
    if (!isResizing) {
      return undefined;
    }
    const onMove = (e: MouseEvent) => {
      e.preventDefault();
      applyWidth(e.clientX);
    };
    const onUp = () => {
      setIsResizing(false);
      const node = elemRef.current;
      const raw = node?.style.getPropertyValue(RIGHT_PANEL_WIDTH_VAR);
      const px = raw ? parseInt(raw, 10) : NaN;
      if (!Number.isNaN(px) && onResizeEnd) {
        onResizeEnd(px);
      }
    };
    document.addEventListener('mousemove', onMove, false);
    document.addEventListener('mouseup', onUp, false);
    return () => {
      document.removeEventListener('mousemove', onMove, false);
      document.removeEventListener('mouseup', onUp, false);
    };
  }, [isResizing, applyWidth, elemRef, onResizeEnd]);

  const startResizing = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
  }, []);

  return (
    <>
      <div className={css.resize} onMouseDown={startResizing} />
      {isResizing && <div className={css.dragOverlay} />}
    </>
  );
};
